import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';
import { useScrollLock } from '../../hooks/useScrollLock';

/**
 * Modal — the CRM's one dialog (spec §3.13), portaled to <body>. A centred card from 640px,
 * a bottom sheet below it; `variant="drawer"` anchors the same chrome to the right edge
 * (Student 360 quick view). Overlay 45% black (+3px blur from 640px); panel radius 28, padding
 * 24 (28 from 640), no divider lines; header `h2` + a 36px round close button; footer
 * right-aligned, secondary then primary.
 *
 *   <Modal open={open} onClose={close} title="Yangi tarif" size="md"
 *          footer={<><button className="ds-btn ds-btn-secondary" onClick={close}>Bekor qilish</button>
 *                    <button className="ds-btn ds-btn-primary" onClick={save}>Saqlash</button></>}>
 *     …fields…
 *   </Modal>
 *
 * Behaviour: focus moves in (an element marked `data-autofocus`, else the first field or
 * button of the body) and returns to what opened it; Tab stays inside; Esc closes — only the
 * top-most dialog, and not when a menu or picker is open over it; clicking the backdrop closes;
 * the page behind does not scroll (ref-counted lock); `aria-modal` + `aria-labelledby`.
 *
 * A form dialog: `as="form"` puts the header, body and footer inside one <form>, so a
 * `type="submit"` button in the footer submits it (no `form="id"` wiring) and Enter in a field does
 * too. `onSubmit` runs with the default already prevented; `formProps` (noValidate, id, ...) go to
 * the <form>.
 *
 *   <Modal open={open} onClose={close} title="Yangi mijoz" as="form" onSubmit={save}
 *          footer={<><button type="button" className="ds-btn ds-btn-secondary" onClick={close}>Bekor qilish</button>
 *                    <button type="submit" className="ds-btn ds-btn-primary">Saqlash</button></>}>
 *     …fields…
 *   </Modal>
 *
 * Props: open, onClose, title, description (read by screen readers), size ('sm' 384 | 'md' 512 |
 * 'lg' 672 | 'xl' 880), variant ('dialog' | 'drawer'), footer, hideClose, closeOnBackdrop,
 * as ('div' | 'form'), onSubmit, formProps, labelledBy / describedBy (ids, when you render your
 * own heading), aria-label, className.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Dialogs that are open, innermost last: only the top one answers Escape.
const openStack = [];

function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  variant = 'dialog',
  footer,
  children,
  className = '',
  hideClose = false,
  closeOnBackdrop = true,
  as = 'div',
  onSubmit,
  formProps,
  labelledBy,
  describedBy,
  'aria-label': ariaLabel,
}) {
  const { t } = useI18n();
  const uid = useId();
  const titleId = `${uid}-title`;
  const descId = `${uid}-desc`;
  const panelRef = useRef(null);
  const previousFocus = useRef(null);
  // The parent often passes an inline onClose: a ref keeps the effects below from re-running on
  // every parent render (their cleanup would hand focus back to the opener mid-typing).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useScrollLock(open);

  useEffect(() => {
    if (!open) return undefined;
    const me = {};
    openStack.push(me);
    previousFocus.current = document.activeElement;

    // Move focus in once the panel exists.
    const panel = panelRef.current;
    if (panel) {
      const target = panel.querySelector('[data-autofocus]')
        || panel.querySelector('.ui-modal__body input:not([type="hidden"]), .ui-modal__body textarea, .ui-modal__body select, .ui-modal__body button, .ui-modal__footer button')
        || panel.querySelector(FOCUSABLE)
        || panel;
      if (typeof target.focus === 'function') target.focus({ preventScroll: true });
    }

    const onKeyDown = (e) => {
      if (openStack[openStack.length - 1] !== me) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll(FOCUSABLE)).filter((el) => el.tabIndex >= 0);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!panelRef.current.contains(active)) {
        // Focus escaped (to <body>): pull it back in.
        if (active === document.body) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        }
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      const at = openStack.indexOf(me);
      if (at >= 0) openStack.splice(at, 1);
      const back = previousFocus.current;
      if (back && typeof back.focus === 'function' && document.contains(back)) back.focus({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;

  const drawer = variant === 'drawer';
  const asForm = as === 'form';
  const panelClass = [
    'ui-modal',
    drawer ? 'ui-modal--drawer' : `ui-modal--${size}`,
    className,
  ].filter(Boolean).join(' ');

  const content = (
    <>
      {(title || !hideClose) && (
        <div className="ui-modal__header">
          {title ? <h2 id={titleId} className="ui-modal__title">{title}</h2> : <span />}
          {!hideClose && (
            <button
              type="button"
              className="ui-modal__close"
              onClick={() => onCloseRef.current?.()}
              aria-label={t('ui.close')}
            >
              <X aria-hidden="true" />
            </button>
          )}
        </div>
      )}
      <div className="ui-modal__body">
        {description && <p id={descId} className="visually-hidden">{description}</p>}
        {children}
      </div>
      {footer && <div className="ui-modal__footer">{footer}</div>}
    </>
  );

  return createPortal(
    // A portal's React events bubble through the React tree, so a click in here would reach a
    // clickable wrapper around <Modal>: stop it at the overlay (the backdrop's own handler has
    // already run). Keys are NOT stopped: this component's Escape listener lives on `window`.
    <div className={`ui-modal-overlay${drawer ? ' ui-modal-overlay--drawer' : ''}`} onClick={(e) => e.stopPropagation()}>
      <div
        className="ui-modal-backdrop"
        onClick={closeOnBackdrop ? () => onCloseRef.current?.() : undefined}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        className={panelClass}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy || (title ? titleId : undefined)}
        aria-describedby={describedBy || (description ? descId : undefined)}
        aria-label={!labelledBy && !title ? ariaLabel : undefined}
        tabIndex={-1}
      >
        {asForm ? (
          <form
            noValidate
            {...formProps}
            className={`ui-modal__form${formProps?.className ? ` ${formProps.className}` : ''}`}
            onSubmit={(e) => {
              e.preventDefault();
              if (onSubmit) onSubmit(e);
            }}
          >
            {content}
          </form>
        ) : content}
      </div>
    </div>,
    document.body,
  );
}

export default Modal;
